import { useCallback } from "react";
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";

import { REVIEW_LINE_STATUSES } from "@/api/contracts/spec-editor";
import type { EditorCacheTarget } from "@/hooks/spec-editor-cache";
import {
  specificationLinesQueryKey,
  specificationReviewLinesQueryKey,
  type SpecLinesPage,
} from "@/hooks/specifications";

// Where an Editor verb acts (#805; ADR-0014 decision 4 as amended): the spec,
// the version, and the caller's view of it — the scope every verb's browser
// input carries and the cache updater corrects. Derived in one place, over the
// lines loader's own cache, so the cursor the server corrects is read the same
// way by every Editor surface (#805–#811).
//
// **Read at the moment of the write**, not at render: the hook hands back a
// getter, so a page that loaded between the render and the click is the one
// the verb names.

/** The view's last loaded page; `null` with none loaded. */
function lastPage(queryClient: QueryClient, key: readonly unknown[]): SpecLinesPage | null {
  const data = queryClient.getQueryData<InfiniteData<SpecLinesPage | null, number>>(key);
  if (!data) return null;
  for (let index = data.pages.length - 1; index >= 0; index -= 1) {
    const page = data.pages[index];
    if (page) return page;
  }
  return null;
}

/**
 * The target for the view the box shows: the base view while it is empty, the
 * term as typed otherwise (no trim — spaces are a term), with the base view's
 * cursor beside a search's, `null` when the base view is not cached. Each
 * cursor is the one the view's last loaded page answered with.
 *
 * A search also names its **snapshot** (#852/D2, #910): the groups its last
 * loaded page holds, in their order — the first page's, as every result has
 * corrected them since — which the server decides the edit from. Left out when
 * the search view is not cached, so the server falls back to the groups the
 * term matches; this is the one place a scope is built, so no caller can
 * forget it.
 */
export function specEditorTarget(
  queryClient: QueryClient,
  specificationId: number,
  versionId: number,
  search: string,
): EditorCacheTarget {
  const base = lastPage(queryClient, specificationLinesQueryKey(specificationId, versionId));
  if (search === "") {
    return { specificationId, versionId, scope: { kind: "base", cursor: base?.cursor ?? 0 } };
  }
  const searched = lastPage(
    queryClient,
    specificationLinesQueryKey(specificationId, versionId, search),
  );
  return {
    specificationId,
    versionId,
    scope: {
      kind: "search",
      term: search,
      cursor: searched?.cursor ?? 0,
      baseCursor: base?.cursor ?? null,
      ...(searched ? { snapshotGroupIds: searched.groups.map((group) => group.id) } : {}),
    },
  };
}

/**
 * The target for the Review & Publish dialog's step 1 (#950): the review
 * view's last loaded page's cursor (0 with none), the base view's beside it
 * (`null` when the base view is not cached), and the statuses the review read
 * asks for — the one constant, so the view a result corrects is the one the
 * dialog shows.
 */
export function specReviewTarget(
  queryClient: QueryClient,
  specificationId: number,
  versionId: number,
): EditorCacheTarget {
  const base = lastPage(queryClient, specificationLinesQueryKey(specificationId, versionId));
  const review = lastPage(
    queryClient,
    specificationReviewLinesQueryKey(specificationId, versionId, REVIEW_LINE_STATUSES),
  );
  return {
    specificationId,
    versionId,
    scope: {
      kind: "review",
      cursor: review?.cursor ?? 0,
      baseCursor: base?.cursor ?? null,
      statuses: [...REVIEW_LINE_STATUSES],
    },
  };
}

/** The Editor's write target, read when a write is sent. */
export type SpecEditorTarget = () => EditorCacheTarget;

/** `specEditorTarget` for the page's spec, version and search, read on call. */
export function useSpecEditorTarget(
  specificationId: number,
  versionId: number,
  search: string,
): SpecEditorTarget {
  const queryClient = useQueryClient();
  return useCallback(
    () => specEditorTarget(queryClient, specificationId, versionId, search),
    [queryClient, specificationId, versionId, search],
  );
}

/** `specReviewTarget` for the dialog's spec and version, read on call. */
export function useSpecReviewTarget(specificationId: number, versionId: number): SpecEditorTarget {
  const queryClient = useQueryClient();
  return useCallback(
    () => specReviewTarget(queryClient, specificationId, versionId),
    [queryClient, specificationId, versionId],
  );
}
